import { Rng } from "./rng";
import type { GameState, LogEntry } from "./state";

/**
 * The company whose command is running. Online, each player's commands are previewed in their own browser
 * and replayed on the server after the other players' commands, so anything a command draws (ids, dice)
 * must not depend on what the others did first: inside a scope, ids count per company and random
 * streams are per company.
 */
let scope: string | null = null;

/**
 * A copy with every object's keys in sorted order. Commands travel through the database, which reorders keys;
 * putting them in one order before they are applied means the browser, the server and any replay see the same input.
 */
export function canonical<T>(v: T): T {
  if (Array.isArray(v)) return v.map(canonical) as T;
  if (v && typeof v === "object") {
    const out: Record<string, unknown> = {};
    for (const k of Object.keys(v as object).sort()) {
      const x = (v as Record<string, unknown>)[k];
      if (x !== undefined) out[k] = canonical(x);
    }
    return out as T;
  }
  return v;
}

export function inScope<T>(companyId: string, f: () => T): T {
  const prev = scope;
  scope = companyId;
  try {
    return f();
  } finally {
    scope = prev;
  }
}

export function rng(state: GameState, stream: string): Rng {
  return new Rng(state.rng, scope ? `${stream}@${scope}` : stream);
}

export function newId(state: GameState, prefix: string): string {
  if (scope) {
    const ids = (state.idsBy ??= {});
    ids[scope] = (ids[scope] ?? 0) + 1;
    return `${prefix}${scope}.${ids[scope].toString(36)}`;
  }
  state.nextId += 1;
  return `${prefix}${state.nextId.toString(36)}`;
}

export function log(state: GameState, kind: string, text: string, company?: string): void {
  const e: LogEntry = { week: state.week, kind, text };
  if (company) e.company = company;
  state.log.push(e);
}

export function clamp(v: number, lo = 0, hi = 1): number {
  return v < lo ? lo : v > hi ? hi : v;
}

export function round1(v: number): number {
  return Math.round(v * 10) / 10;
}

export function round2(v: number): number {
  return Math.round(v * 100) / 100;
}

export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * t;
}

/** Piecewise-linear lookup in [[x, y], ...] sorted by x. */
export function interp(points: [number, number][], x: number): number {
  if (x <= points[0][0]) return points[0][1];
  for (let i = 1; i < points.length; i++) {
    const [x1, y1] = points[i];
    if (x <= x1) {
      const [x0, y0] = points[i - 1];
      return y0 + ((y1 - y0) * (x - x0)) / (x1 - x0);
    }
  }
  return points[points.length - 1][1];
}

/**
 * 12,345 with thousands commas. Not toLocaleString: the server's runtime has different locale data,
 * and text in the log is part of the world, so it must come out the same everywhere.
 */
export function digits(n: number): string {
  const s = String(Math.abs(Math.round(n)));
  return (n < 0 ? "-" : "") + s.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

export function money(n: number): string {
  const sign = n < 0 ? "-" : "";
  const a = Math.abs(n);
  // "$16K", not "$16.0K"
  if (a >= 1e6) return `${sign}$${(a / 1e6).toFixed(a >= 1e7 ? 0 : 1).replace(/\.0$/, "")}M`;
  if (a >= 1e4) return `${sign}$${(a / 1e3).toFixed(a >= 1e5 ? 0 : 1).replace(/\.0$/, "")}K`;
  if (a < 10 && a !== Math.round(a)) return `${sign}$${a.toFixed(2)}`;
  return `${sign}$${digits(Math.round(a))}`;
}
